<template>
  <v-select
    v-model="template"
    :items="page.templates"
    item-text="name"
    item-value="id"
    label="Template personnalisé"
    outlined
    :dense="dense"
    hide-details
    clearable
  />
</template>

<script>
import {
  computed,
  onMounted,
  reactive,
  set,
  watch,
} from "@vue/composition-api";
import isEqual from "lodash/isEqual";

import useAxios from "@/hooks/useAxios";
import useProxy from "@/hooks/useProxy";
import useQueryString from "@/hooks/useQueryString";

export default {
  name: "CustomPromptTemplateFilter",
  components: {},
  props: {
    initialValue: {
      type: Number,
      default: 0,
      validator(value) {
        return !isNaN(value);
      },
    },
    store: {
      type: String,
      validator(value) {
        /* List of stores having :
        - getters["<store>/<storeGetter>"]
        - dispatch("<store>/<storeUpdater>", payload)
        */
        return ["aiFactory"].includes(value);
      },
    },
    storeGetter: {
      type: String,
      default: "getCustomPromptTemplate",
    },
    storeUpdater: {
      type: String,
      default: "updateCustomPromptTemplate",
    },
    noUrl: {
      type: Boolean,
      default: false,
    },
    forceSiteId: {
      type: Number,
      default: 0,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    preselectDefault: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const { axiosGet } = useAxios();
    const { store } = useProxy();

    const { addQueryStringParam, synchronizeFilterWithQueryString } =
      useQueryString();

    const page = reactive({
      isInitialized: false,
      initialUrlValue: "", // in case we need to load from url params
      templates: [],
    });

    // =>  METHODS
    const initTemplates = async () => {
      if (viaStore.value && props.preselectDefault) {
        store.dispatch(`${props.store}/updateCustomPromptTemplate`, null);
      }

      const { data } = await axiosGet("/api/ai-factory/custom-templates", {
        ...(props.forceSiteId > 0
          ? { site_id: props.forceSiteId }
          : site.value
          ? { site_id: site.value }
          : {}),
      });

      page.templates = data.templates;

      // Stocker aussi dans le store pour les autres composants
      if (viaStore.value) {
        store.dispatch(
          `${props.store}/updateCustomPromptTemplates`,
          data.templates
        );

        if (props.preselectDefault) {
          const siteSpecificDefault = data.templates.find(
            (t) => t.is_default && t.site_id === props.forceSiteId
          );
          const fallbackDefault = data.templates.find((t) => t.is_default);
          const defaultTemplate = siteSpecificDefault || fallbackDefault;
          if (defaultTemplate) {
            store.dispatch(
              `${props.store}/updateCustomPromptTemplate`,
              defaultTemplate.id
            );
          }
        }
      }
    };

    // =>  COMPUTED
    const viaStore = computed(() => props.store !== undefined);
    const initialValue = computed(() =>
      props.noUrl
        ? props.initialValue
        : page.initialUrlValue ?? props.initialValue
    );
    const site = computed(() => store.getters["newsRadar/getSites"]);

    const template = computed({
      get: () => {
        if (viaStore.value) {
          return store.getters[`${props.store}/getCustomPromptTemplate`];
        }

        return initialValue.value;
      },
      set: (value) => {
        if (viaStore.value) {
          store.dispatch(`${props.store}/updateCustomPromptTemplate`, value);

          if (page.isInitialized && !props.noUrl) {
            // Only update user changes, not initialization
            addQueryStringParam({
              param: "template",
              value: value,
            });
          }
        }

        if (!isEqual(value, initialValue.value)) {
          emit("valueChanged", value);
        }
      },
    });

    // =>  WATCH

    watch(
      () => props.forceSiteId,
      async () => {
        await initTemplates();
      }
    );

    // =>  HOOKS
    onMounted(async () => {
      await initTemplates();

      if (viaStore.value && !props.noUrl) {
        set(
          page,
          "initialUrlValue",
          await synchronizeFilterWithQueryString({
            dispatcher: "aiFactory/updateCustomPromptTemplate",
            param: "template",
            value: template.value,
          })
        );
      }

      page.isInitialized = true;
    });

    // =>  DATA
    return {
      page,
      template,
    };
  },
};
</script>

<style></style>
